import {
  type TerminalAttachment,
  type TerminalClientMessage,
  TerminalClientMessageType,
  type TerminalServerMessage,
  TerminalServerMessageType,
} from '@this+that/models';
import log from 'loglevel';

import { uploadAttachment } from './attachmentUpload';
import { nextBackoffMs } from './backoff';
import {
  createTerminalTransport,
  resolveTerminalTransportKind,
} from './createTerminalTransport';
import {
  type TerminalGateway,
  TerminalSendError,
  type TerminalTransport,
} from './types';
import {
  TerminalConnectionStatus,
  TerminalSendFailure,
} from '../../../enums/terminal';
import { useTerminalStore } from '../../../store/Terminal/useTerminalStore';
import {
  TERMINAL_ENTRY_GAP_MS,
  TERMINAL_SESSIONS_GRACE_MS,
  TERMINAL_SESSIONS_LOAD_TIMEOUT_MS,
} from '../../../utils/constants/terminalConstants';

export type TerminalErrorHandler = (
  message: Extract<
    TerminalServerMessage,
    { type: TerminalServerMessageType.ERROR }
  >,
) => void;

let activeTransport: TerminalTransport | null = null;
let activeGateway: TerminalGateway | null = null;
let activeOnError: TerminalErrorHandler | null = null;
let activeGetPreferred: (() => string | null) | null = null;
let generation = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
interface GapWatch {
  timer: ReturnType<typeof setTimeout>;
  lastSeq: number;
  hasRequestedReplay: boolean;
}

const gapWatches = new Map<string, GapWatch>();

function clearGapWatches(): void {
  gapWatches.forEach((watch) => clearTimeout(watch.timer));
  gapWatches.clear();
}

function hasGap(sessionId: string): boolean {
  const session = useTerminalStore.getState().sessions[sessionId];
  return Boolean(session) && Object.keys(session.reorderBuffer).length > 0;
}

function onGapTimeout(transport: TerminalTransport, sessionId: string): void {
  const watch = gapWatches.get(sessionId);
  gapWatches.delete(sessionId);
  if (!watch || !hasGap(sessionId)) {
    return;
  }
  const { lastSeq } = useTerminalStore.getState().sessions[sessionId];
  const hasProgressed = lastSeq !== watch.lastSeq;
  if (!hasProgressed && watch.hasRequestedReplay) {
    useTerminalStore.getState().flushReorderBuffer(sessionId);
    return;
  }
  if (!hasProgressed) {
    sendQuietly(transport, {
      type: TerminalClientMessageType.SESSION_REPLAY,
      sessionId,
      fromSeq: lastSeq,
    });
  }
  watchGap(transport, sessionId, !hasProgressed);
}

function watchGap(
  transport: TerminalTransport,
  sessionId: string,
  hasRequestedReplay = false,
): void {
  if (!hasGap(sessionId)) {
    const existing = gapWatches.get(sessionId);
    if (existing) {
      clearTimeout(existing.timer);
      gapWatches.delete(sessionId);
    }
    return;
  }
  if (gapWatches.has(sessionId)) {
    return;
  }
  gapWatches.set(sessionId, {
    timer: setTimeout(
      () => onGapTimeout(transport, sessionId),
      TERMINAL_ENTRY_GAP_MS,
    ),
    lastSeq: useTerminalStore.getState().sessions[sessionId].lastSeq,
    hasRequestedReplay,
  });
}

let loadingTimers: Array<ReturnType<typeof setTimeout>> = [];

function clearLoadingTimers(): void {
  loadingTimers.forEach(clearTimeout);
  loadingTimers = [];
}

function finishLoadingAfter(delayMs: number): void {
  loadingTimers.push(
    setTimeout(
      () => useTerminalStore.getState().finishLoadingSessions(),
      delayMs,
    ),
  );
}

function sendQuietly(
  transport: TerminalTransport,
  message: TerminalClientMessage,
): void {
  Promise.resolve(transport.send(message)).catch((error) =>
    log.debug('terminalConnection: resync send failed', error),
  );
}

export function resyncAfterConnect(transport: TerminalTransport): void {
  const { sessions } = useTerminalStore.getState();
  sendQuietly(transport, { type: TerminalClientMessageType.FOLDERS_LIST });
  sendQuietly(transport, { type: TerminalClientMessageType.SESSION_LIST });
  for (const [sessionId, session] of Object.entries(sessions)) {
    sendQuietly(transport, {
      type: TerminalClientMessageType.SESSION_REPLAY,
      sessionId,
      fromSeq: session.lastSeq,
    });
  }
}

function wireTransport(
  transport: TerminalTransport,
  onError: TerminalErrorHandler,
): void {
  transport.onMessage((message) => {
    if (message.type === TerminalServerMessageType.ERROR) {
      onError(message);
      return;
    }
    if (message.type === TerminalServerMessageType.FOLDERS) {
      finishLoadingAfter(TERMINAL_SESSIONS_GRACE_MS);
    }
    if (message.type === TerminalServerMessageType.FOLDERS_PICKED) {
      useTerminalStore.getState().markOpenRequested();
      sendQuietly(transport, {
        type: TerminalClientMessageType.SESSION_OPEN,
        cwd: message.folder.path,
      });
    }
    useTerminalStore.getState().applyServerMessage(message);
    if (message.type === TerminalServerMessageType.SESSION_ENTRY) {
      watchGap(transport, message.sessionId);
    }
  });
  transport.onStatus((status) => {
    const store = useTerminalStore.getState();
    store.setConnection(status);
    store.setTransportKind(transport.getKind());
    store.setActiveDevice(transport.getDeviceId?.() ?? null);
    if (status === TerminalConnectionStatus.ONLINE) {
      finishLoadingAfter(TERMINAL_SESSIONS_LOAD_TIMEOUT_MS);
      resyncAfterConnect(transport);
    }
  });
}

function scheduleCreationRetry(retry: () => void, attempt: number): void {
  retryTimer = setTimeout(retry, nextBackoffMs(attempt));
}

export async function ensureTerminalConnection(
  gateway: TerminalGateway,
  onError: TerminalErrorHandler,
  getPreferredDeviceId: () => string | null,
  attempt = 0,
): Promise<void> {
  if (activeTransport) {
    return;
  }
  generation += 1;
  const current = generation;
  activeGateway = gateway;
  activeOnError = onError;
  activeGetPreferred = getPreferredDeviceId;
  const kind = resolveTerminalTransportKind(
    import.meta.env.VITE_TERMINAL_TRANSPORT,
  );
  try {
    const transport = await createTerminalTransport(
      kind,
      gateway,
      getPreferredDeviceId(),
    );
    if (current !== generation) {
      transport.close();
      return;
    }
    activeTransport = transport;
    wireTransport(transport, onError);
    const store = useTerminalStore.getState();
    store.setTransportKind(transport.getKind());
    store.setActiveDevice(transport.getDeviceId?.() ?? null);
    transport.connect();
  } catch (error) {
    if (current !== generation) {
      return;
    }
    log.error('terminalConnection: failed to create transport', error);
    useTerminalStore.getState().setConnection(TerminalConnectionStatus.OFFLINE);
    scheduleCreationRetry(
      () =>
        void ensureTerminalConnection(
          gateway,
          onError,
          getPreferredDeviceId,
          attempt + 1,
        ),
      attempt,
    );
  }
}

function teardownActiveTransport(): void {
  generation += 1;
  clearLoadingTimers();
  clearGapWatches();
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  activeTransport?.close();
  activeTransport = null;
}

export async function switchTerminalDevice(): Promise<void> {
  if (!activeGateway || !activeOnError || !activeGetPreferred) {
    return;
  }
  const gateway = activeGateway;
  const onError = activeOnError;
  const getPreferred = activeGetPreferred;
  teardownActiveTransport();
  useTerminalStore.getState().resetDeviceSessions();
  await ensureTerminalConnection(gateway, onError, getPreferred);
}

export async function sendTerminalMessage(
  message: TerminalClientMessage,
): Promise<void> {
  if (!activeTransport) {
    log.warn('terminalConnection: dropped message, no transport', message.type);
    throw new TerminalSendError(TerminalSendFailure.DEVICE_OFFLINE);
  }
  await activeTransport.send(message);
}

export function closeTerminalConnection(): void {
  teardownActiveTransport();
  activeGateway = null;
  activeOnError = null;
  activeGetPreferred = null;
}

export async function uploadTerminalAttachment(
  file: File,
  onProgress: (fraction: number) => void,
): Promise<TerminalAttachment> {
  if (activeTransport?.uploadAttachment) {
    return activeTransport.uploadAttachment(file, onProgress);
  }
  const deviceId = activeTransport?.getDeviceId?.() ?? null;
  if (!activeGateway || !deviceId) {
    throw new TerminalSendError(TerminalSendFailure.DEVICE_OFFLINE);
  }
  return uploadAttachment(
    deviceId,
    file,
    activeGateway.createAttachmentUpload,
    onProgress,
  );
}

export function getTerminalDeviceId(): string | null {
  return activeTransport?.getDeviceId?.() ?? null;
}
